<template>
  <div
    class="flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
    data-v0-t="card"
  >
    <div class="flex flex-col space-y-1.5 p-6">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-2xl font-semibold leading-none tracking-tight">
            Alert Recipients
          </h3>
          <p class="text-sm text-muted-foreground">
            Manage users who can receive WhatsApp alerts
          </p>
        </div>
        <el-button @click="handleEditModel()" type="primary">
          <span class="flex flex-row items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-user-plus mr-2 h-4 w-4"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <line x1="19" x2="19" y1="8" y2="14"></line>
              <line x1="22" x2="16" y1="11" y2="11"></line>
            </svg>
            Add Recipient
          </span>
        </el-button>
      </div>
    </div>
    <div class="flex-1 flex flex-col p-6 pt-0 overflow-hidden">
      <el-table
        v-loading="tableCtrl.state.loading"
        element-loading-background="rgba(0, 0, 0, 0.8)"
        :data="tableCtrl.state.filteredData"
        border
        height="auto"
        style="width: 100%"
      >
        <el-table-column prop="name" label="Name" />
        <el-table-column prop="phone" label="Phone Number" />
        <el-table-column prop="role" label="Role">
          <template #default="scope">
            {{ scope.row.role === "admin" ? "Admin" : "Staff" }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="Status">
          <template #default="scope">
            <div
              class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
              :class="{
                'bg-green-100 text-green-800': 'Active' === 'Active',
                'bg-red-100 text-red-800': scope.row.status !== 'Active',
              }"
            >
              Active
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Actions">
          <template #default="{ row }">
            <ActionButtons
              @command="handleCommand"
              :item="row"
              :actions="actions"
            />
          </template>
        </el-table-column>
      </el-table>
      <div class="flex mt-4 justify-end">
        <el-pagination
          @size-change="(val) => tableCtrl.setPageSize(val)"
          @current-change="(val) => tableCtrl.setCurrentPage(val)"
          :current-page="tableCtrl.state.pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="tableCtrl.state.pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="tableCtrl.state.pagination.total"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ActionButtons from "@/components/ActionButtons.vue";
import { TableController } from "@/controllers/TableController";
import {
  listNotificationRecipients,
  deleteNotificationRecipient,
  testNotificationTemplate,
} from "@/api";
import { Loading } from 'element-ui';
const normalizeSearchValue = (value) => {
  const str = typeof value === "string" ? value.trim() : "";
  return str || undefined;
};

export default {
  name: "RecipientsView",
  components: { ActionButtons },
  props: {
    searchText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      tableCtrl: new TableController({
        loadMethod: listNotificationRecipients,
        autoLoad: true,
        isClientPaged: true,
        pageSize: 10,
        keyField: "id",
        queryParameters: {
          search: normalizeSearchValue(this.searchText),
        },
      }),
    };
  },
  computed: {
    actions: () => [
      {
        type: "dropdown",
        label: "more",
        icon: "el-icon-more",
        items: [
          { label: "Edit", command: "edit", icon: "el-icon-edit" },
          {
            label: "Send Test Message",
            command: "test",
            icon: "el-icon-s-promotion",
          },
          {
            label: "Remove",
            command: "delete",
            icon: "el-icon-delete",
            color: "#F56C6C",
            divided: true,
          },
        ],
      },
    ],
  },
  methods: {
    applyServerSearch(value) {
      if (!this.tableCtrl) {
        return;
      }
      const normalized = normalizeSearchValue(value);
      if (this.tableCtrl.queryParameters.search === normalized) {
        return;
      }
      this.tableCtrl.queryParameters.search = normalized;
      this.tableCtrl.refresh();
    },
    mockData() {
      const roles = ["admin", "staff"];
      const statuses = ["Active", "Inactive"];
      return Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        name: `User ${i + 1}`,
        phone: `+1234567890${i + 1}`,
        role: roles[i % roles.length],
        status: statuses[i % statuses.length],
      }));
    },
    async handleCommand({ command, item }) {
      if (command === "edit") {
        await this.handleEditModel(item);
        await this.tableCtrl.refresh();
        this.$message.success("Recipient saved successfully");
      } else if (command === "delete") {
        await this.handleDelete(item);
        await this.tableCtrl.refresh();
        this.$message.success("Recipient removed");
      } else if (command === "test") {
        await this.handleTest(item);
      }
    },
    async handleEditModel(item) {
      const me = this;
      await me.$loaderWindow({
        title: item ? `Edit Recipient` : `Add New Recipient`,
        component: () => import("./EditRecipient.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { id: item?.id },
      });
      await me.tableCtrl.refresh();
    },
    async handleDelete(item) {
      const me = this;
      await me.$confirm(
        `Are you sure you want to delete recipient ${item?.name}?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteNotificationRecipient(item.id);
    },
    async handleTest(item) {
      if (!item?.id) {
        this.$message.error("Recipient ID is missing.");
        return;
      }
      const loading = Loading.service({
        lock: true,
        text: "Sending test message...",
        background: "rgba(0, 0, 0, 0.4)",
      });
      try {
        const response = await testNotificationTemplate({
          recipientId: item.id,
        });
        const html = this.formatTestResult(response);
        await this.$alert(html, "Test Message Result", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
        });
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to send test message.";
        this.$message.error(message);
      } finally {
        loading.close();
      }
    },
    formatTestResult(result) {
      if (!result) {
        return "<p>No response data.</p>";
      }
      if (Array.isArray(result.messages)) {
        const rows = result.messages
          .map(
            (msg, index) =>
              `<div style="padding:8px 0;border-bottom:1px solid rgba(148,163,184,0.3);">
                <div><strong>#${index + 1}</strong> · ${
                  msg.recipientName || msg.recipientId || "Recipient"
                }</div>
                <div>Status: ${msg.status || "--"} (${msg.success ? "success" : "failed"})</div>
                <div style="margin-top:4px;color:#94a3b8;">${
                  msg.message || "--"
                }</div>
              </div>`
          )
          .join("");
        return `
          <div>
            <div style="margin-top:8px;">${rows}</div>
          </div>`;
      }
      return `
        <div>
          <p><strong>Recipient:</strong> ${
            result.recipientName || result.recipientId || "--"
          }</p>
          <p><strong>Status:</strong> ${result.status || "--"} (${
        result.success ? "success" : "failed"
      })</p>
          <div style="margin-top:8px;color:#94a3b8;">${
            result.message || "--"
          }</div>
        </div>`;
    },
  },
  watch: {
    searchText: {
      handler(value) {
        this.applyServerSearch(value);
      },
    },
  },
};
</script>
